import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import * as moment from 'moment';
import { DictationActivityEvent } from '../shared/models';
import { PipelineActivityLogService } from '../shared/services/pipeline-activity-log.service';
import { AudioDictationService } from '../shared/services/audio-dictation.service';
import { MedicalNecessityPdfService } from '../shared/services/medical-necessity-pdf.service';
import { ClinicalDocumentsPdfService } from '../shared/services/clinical-documents-pdf.service';
import { DocumentViewerModalComponent } from '../shared/components/document-viewer-modal/document-viewer-modal.component';
import { formatPatientName, getAvatarColor, getInitials } from '../shared/utils/avatar.util';
import {
  getMedicalNecessityIndicator,
  getMedicalNecessityIndicatorLabel,
  MedicalNecessityIndicator,
  buildCoverageDetails,
} from '../shared/utils/medical-necessity.util';

// One row per unique patient in the Patients panel — derived from the activity-log feed,
// deduped down to each patient's most recent activity.
interface PatientRow {
  patientId: string;
  patientName: string;
  patientMrn: string;
  lastActivityAt: string;
}

// One row per available document for a dictation in the Files panel — a plain list with
// View/Download actions, no inline content. Mirrors Live Operations' Files panel exactly.
interface FileRow {
  key: string;
  icon: string;
  label: string;
  subtitle?: string;
  itemId?: string;
  view: () => void;
  download: () => void;
}

interface PatientDictation {
  id: string;
  provider?: { description: string };
  created_at: string;
  transcription?: string;
  doctorNotesParsed?: any;
  billingParsed?: any;
  action_items: any[];
  fileRows: FileRow[];
}

@Component({
  selector: 'app-ort-masters',
  templateUrl: './ort-masters.component.html',
  styleUrls: ['./ort-masters.component.scss'],
})
export class OrtMastersComponent implements OnInit, OnDestroy {
  // Dashboard mode: 'live' auto-follows the most recently active patient (top of the Patients
  // panel); 'static' freezes on whatever patient was last clicked there. Mirrors Live
  // Operations' own live/static toggle exactly, but independently — this page has its own
  // Patients panel driving it rather than syncing with Live Operations' log feed.
  mode: 'live' | 'static' = 'live';

  currentPatientId: string | null = null;
  currentPatientName = '';
  currentPatientMrn = '';
  private selectedProviderId: string | null = null;

  lastUpdated: Date = new Date();
  private logsInterval: any;
  private pollInterval: any;

  // Left column, row 1 (35%) — Patients: an infinite-scroll activity-log feed (same mechanics
  // as Live Operations' log feed — merge fresh at the top on poll, append older pages at the
  // bottom on scroll), deduped down to one row per patient (most recent activity wins).
  logs: DictationActivityEvent[] = [];
  patients: PatientRow[] = [];
  logsLoading = true;
  logsLoadingMore = false;
  private logsNextPage: number | null = null;
  private readonly logsPageSize = 40;
  // Activity logs are dense with non-patient-linked events (insurance/order/etc.), so a single
  // page can dedupe down to very few unique patients — sometimes just one — leaving the panel
  // too short to ever get a scrollbar. With no scrollbar, (scroll) never fires and further
  // pages become unreachable. See ensureEnoughPatients().
  private readonly minPatientsToShow = 10;

  // Left column, row 2 (35%) — Action Items: infinite scroll, read-only (no selection — the
  // Patients panel is what drives which patient's data is shown everywhere on this page now).
  actionItems: any[] = [];
  actionItemsLoading = true;
  actionItemsLoadingMore = false;
  total = 0;
  private actionItemsNextPage: number | null = null;
  private readonly actionItemsPageSize = 20;
  private actionItemsRequestSeq = 0;

  // Left column, row 3 (30%) — Files: same as Live Operations' Files panel (per-dictation
  // Doctor Notes / Transcription / Summary / Medical Necessity rows with View/Download), but
  // covering the patient's full dictation history rather than just today's.
  filesDictations: PatientDictation[] = [];
  filesLoading = true;
  filesLoadingMore = false;
  private filesNextPage: number | null = null;
  private readonly filesPageSize = 10;
  private filesRequestSeq = 0;
  generatingPdfForItemId: string | null = null;

  // Derived from filesDictations' action items (the richest source we have — it's the only one
  // whose order include carries billing_codes for CPT display) — every action item with an
  // lcd_coverage_details, shown inline as its own document both here and in the Medical
  // Records panel, rather than only being reachable via the file-row's View/Download buttons.
  medicalNecessityItems: any[] = [];

  // Right column (70%) — Medical Records: ORT Masters + Doctor Notes, stacked, for the
  // current/pinned patient, last 6 months.
  rightPanelLoading = false;
  ortMasters: any[] = [];
  doctorNotes: any[] = [];

  // Same right column — MRI / CT / Operative reports for the patient (last 6 months). These
  // tables only ever hold a PDF at `image_url` (the AI-extracted `content` text column is never
  // shown here) — so each is rendered as an embedded PDF, not parsed content. Also surfaced as
  // plain file rows in the Files panel (they aren't tied to any one dictation, so they can't
  // slot into that panel's per-dictation cards).
  mriReports: any[] = [];
  ctReports: any[] = [];
  operativeReports: any[] = [];
  reportFileRows: FileRow[] = [];
  private rightPanelRequestSeq = 0;

  @ViewChild('printDocumentsModal') printDocumentsModalRef!: TemplateRef<any>;

  get printableDocumentCount(): number {
    return this.medicalNecessityItems.length + this.ortMasters.length + this.doctorNotes.length;
  }

  constructor(
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private pipelineActivityLogService: PipelineActivityLogService,
    private audioDictationService: AudioDictationService,
    private medicalNecessityPdfService: MedicalNecessityPdfService,
    private clinicalDocumentsPdfService: ClinicalDocumentsPdfService,
    private modalService: NgbModal,
    private spinnerService: NgxSpinnerService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    document.title = 'Medical Records — Live Sync';

    const params = this.route.snapshot.queryParamMap;
    const patientId = params.get('patient_id');
    this.selectedProviderId = params.get('provider_id');

    if (patientId) {
      this.focusPatient(patientId, params.get('name') || '', params.get('mrn') || '');
    }

    this.fetchLogs();
    this.logsInterval = setInterval(() => this.fetchLogs(), 5 * 1000);
    this.pollInterval = setInterval(() => {
      this.fetchActionItems();
      this.fetchFiles();
    }, 8 * 1000);
  }

  ngOnDestroy(): void {
    clearInterval(this.logsInterval);
    clearInterval(this.pollInterval);
  }

  setMode(mode: 'live' | 'static'): void {
    if (this.mode === mode) return;
    this.mode = mode;
    if (mode === 'live') {
      this.detectCurrentPatient();
    }
  }

  private fetchLogs(): void {
    this.pipelineActivityLogService
      .getActivityLogs({ page: 1, limit: this.logsPageSize, provider_id: this.selectedProviderId ?? undefined })
      .then((res) => {
        this.logsLoading = false;
        const incoming: DictationActivityEvent[] = res?.data?.data ?? [];
        if (!this.logs.length) {
          this.logs = incoming;
          this.logsNextPage = res?.data?.nextPage ?? null;
        } else {
          // Merge fresh logs at the top instead of replacing the list, so pages loaded via
          // infinite scroll aren't wiped out on every poll.
          const existingIds = new Set(this.logs.map((l) => l.id));
          const fresh = incoming.filter((l) => !existingIds.has(l.id));
          if (fresh.length) this.logs = [...fresh, ...this.logs];
        }
        this.lastUpdated = new Date();
        this.patients = this.derivePatientRows(this.logs);
        this.detectCurrentPatient();
        this.ensureEnoughPatients();
      })
      .catch((err) => {
        this.logsLoading = false;
        console.error('Failed to load live logs:', err);
      });
  }

  onLogsScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 150) {
      this.loadMoreLogs();
    }
  }

  /** Eagerly pulls in more log pages — beyond whatever a scroll event has triggered — until
   *  the Patients panel has a reasonable list to show (or activity history runs out). Without
   *  this, a page that dedupes down to only 1-2 patients never grows a scrollbar, so the user
   *  has no way to trigger loadMoreLogs() themselves and further pages are stuck unreachable. */
  private ensureEnoughPatients(): void {
    if (this.patients.length >= this.minPatientsToShow) return;
    if (!this.logsNextPage || this.logsLoadingMore) return;
    this.loadMoreLogs();
  }

  private loadMoreLogs(): void {
    if (this.logsLoadingMore || !this.logsNextPage) return;
    this.logsLoadingMore = true;
    this.pipelineActivityLogService
      .getActivityLogs({ page: this.logsNextPage, limit: this.logsPageSize, provider_id: this.selectedProviderId ?? undefined })
      .then((res) => {
        this.logsLoadingMore = false;
        const incoming: DictationActivityEvent[] = res?.data?.data ?? [];
        // New logs arriving at the top shift page offsets, so dedupe by id.
        const existingIds = new Set(this.logs.map((l) => l.id));
        const older = incoming.filter((l) => !existingIds.has(l.id));
        if (older.length) this.logs = [...this.logs, ...older];
        this.logsNextPage = res?.data?.nextPage ?? null;
        this.patients = this.derivePatientRows(this.logs);
        this.ensureEnoughPatients();
      })
      .catch((err) => {
        this.logsLoadingMore = false;
        console.error('Failed to load more logs:', err);
      });
  }

  // Logs are already ordered most-recent-first, so the first occurrence of each patient_id in
  // iteration order is that patient's latest activity — exactly "currently processing on top,
  // previously processed below" once deduped.
  private derivePatientRows(logs: DictationActivityEvent[]): PatientRow[] {
    const seen = new Set<string>();
    const rows: PatientRow[] = [];
    for (const log of logs) {
      if (!log.patient_id || !log.patient || seen.has(log.patient_id)) continue;
      seen.add(log.patient_id);
      rows.push({
        patientId: log.patient_id,
        patientName: formatPatientName(log.patient.first_name, log.patient.last_name),
        patientMrn: log.patient.med_rec_nbr || '',
        lastActivityAt: log.created_at,
      });
    }
    return rows;
  }

  trackByPatientId(_index: number, row: PatientRow): string {
    return row.patientId;
  }

  private detectCurrentPatient(): void {
    if (this.mode !== 'live') return;
    const top = this.patients[0];
    if (!top) return;
    this.focusPatient(top.patientId, top.patientName, top.patientMrn);
  }

  /** Static-mode click handler — pins whichever patient row was clicked. No-op in live mode,
   *  where the current patient is always whoever's most recently active. */
  selectPatient(row: PatientRow): void {
    if (this.mode !== 'static') return;
    this.focusPatient(row.patientId, row.patientName, row.patientMrn);
  }

  private focusPatient(patientId: string, patientName: string, patientMrn: string): void {
    if (patientId === this.currentPatientId) return;

    this.currentPatientId = patientId;
    this.currentPatientName = patientName;
    this.currentPatientMrn = patientMrn;

    this.resetRightPanel();
    this.actionItems = [];
    this.actionItemsNextPage = null;
    this.actionItemsLoading = true;
    this.filesDictations = [];
    this.filesNextPage = null;
    this.filesLoading = true;
    this.medicalNecessityItems = [];

    this.fetchActionItems();
    this.fetchDocuments();
    this.fetchFiles();
  }

  /** Initial load and periodic poll — fetches page 1 and merges fresh items in at the top
   *  (same pattern as Live Operations' log feed), leaving already-scrolled-in older pages
   *  beneath untouched. Also refreshes in place any of those newest items whose status may
   *  have changed (e.g. a medical necessity indicator flipping from pending to approved). */
  private fetchActionItems(): void {
    if (!this.currentPatientId) return;
    const requestId = ++this.actionItemsRequestSeq;
    this.audioDictationService
      .getActionItemsForPatient(this.currentPatientId, 1, this.actionItemsPageSize, this.selectedProviderId ?? undefined)
      .then((res) => {
        if (requestId !== this.actionItemsRequestSeq) return; // a newer request has since started — drop this stale response
        this.actionItemsLoading = false;
        const data = res?.data;
        const incoming: any[] = data?.data ?? [];
        this.total = data?.total ?? 0;
        this.actionItemsNextPage = data?.nextPage ?? null;

        if (!this.actionItems.length) {
          this.actionItems = incoming;
        } else {
          const incomingById = new Map(incoming.map((i) => [i.id, i]));
          const existingIds = new Set(this.actionItems.map((i) => i.id));
          const fresh = incoming.filter((i) => !existingIds.has(i.id));
          const refreshed = this.actionItems.map((i) => incomingById.get(i.id) ?? i);
          this.actionItems = [...fresh, ...refreshed];
        }
      })
      .catch((err) => {
        if (requestId !== this.actionItemsRequestSeq) return;
        this.actionItemsLoading = false;
        console.error("Failed to load patient's action items:", err);
      });
  }

  onActionItemsScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 150) {
      this.loadMoreActionItems();
    }
  }

  private loadMoreActionItems(): void {
    if (this.actionItemsLoadingMore || !this.actionItemsNextPage || !this.currentPatientId) return;
    this.actionItemsLoadingMore = true;
    this.audioDictationService
      .getActionItemsForPatient(this.currentPatientId, this.actionItemsNextPage, this.actionItemsPageSize, this.selectedProviderId ?? undefined)
      .then((res) => {
        this.actionItemsLoadingMore = false;
        const data = res?.data;
        const incoming: any[] = data?.data ?? [];
        // Newer items arriving at the top (via the poll merge above) shift page offsets,
        // so dedupe by id rather than trusting position.
        const existingIds = new Set(this.actionItems.map((i) => i.id));
        const older = incoming.filter((i) => !existingIds.has(i.id));
        if (older.length) this.actionItems = [...this.actionItems, ...older];
        this.actionItemsNextPage = data?.nextPage ?? null;
        this.total = data?.total ?? this.total;
      })
      .catch((err) => {
        this.actionItemsLoadingMore = false;
        console.error('Failed to load more action items:', err);
      });
  }

  // ── Files panel (Live Operations parity, but full history) ──────────────────────────────

  private fetchFiles(): void {
    if (!this.currentPatientId) return;
    const requestId = ++this.filesRequestSeq;
    this.audioDictationService
      .getDictationsForPatient(this.currentPatientId, 1, this.filesPageSize, this.selectedProviderId ?? undefined)
      .then((res) => {
        if (requestId !== this.filesRequestSeq) return;
        this.filesLoading = false;
        const data = res?.data;
        const incoming: PatientDictation[] = (data?.data ?? []).map((raw: any) => this.mapDictation(raw));
        this.filesNextPage = data?.nextPage ?? null;

        if (!this.filesDictations.length) {
          this.filesDictations = incoming;
        } else {
          const existingIds = new Set(this.filesDictations.map((d) => d.id));
          const fresh = incoming.filter((d) => !existingIds.has(d.id));
          if (fresh.length) this.filesDictations = [...fresh, ...this.filesDictations];
        }
        this.deriveMedicalNecessityItems();
      })
      .catch((err) => {
        if (requestId !== this.filesRequestSeq) return;
        this.filesLoading = false;
        console.error('Failed to load files:', err);
      });
  }

  onFilesScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 150) {
      this.loadMoreFiles();
    }
  }

  private loadMoreFiles(): void {
    if (this.filesLoadingMore || !this.filesNextPage || !this.currentPatientId) return;
    this.filesLoadingMore = true;
    this.audioDictationService
      .getDictationsForPatient(this.currentPatientId, this.filesNextPage, this.filesPageSize, this.selectedProviderId ?? undefined)
      .then((res) => {
        this.filesLoadingMore = false;
        const data = res?.data;
        const incoming: PatientDictation[] = (data?.data ?? []).map((raw: any) => this.mapDictation(raw));
        const existingIds = new Set(this.filesDictations.map((d) => d.id));
        const older = incoming.filter((d) => !existingIds.has(d.id));
        if (older.length) this.filesDictations = [...this.filesDictations, ...older];
        this.filesNextPage = data?.nextPage ?? null;
        this.deriveMedicalNecessityItems();
      })
      .catch((err) => {
        this.filesLoadingMore = false;
        console.error('Failed to load more files:', err);
      });
  }

  /** Every action item across the currently-loaded files pages that has an
   *  lcd_coverage_details, with its coverage text pre-parsed and CPT codes extracted once here
   *  — not recomputed from the template — so *ngFor gets stable array references across change
   *  detection ticks instead of tearing down and rebuilding these cards on every poll. */
  private deriveMedicalNecessityItems(): void {
    const items = this.filesDictations.flatMap((d) =>
      (d.action_items || [])
        .filter((item: any) => item.order?.lcd_coverage_details)
        .map((item: any) => ({
          ...item,
          dictationTime: d.created_at,
          coverageDetails: buildCoverageDetails(item.order?.lcd_coverage_details),
          cptCodes: (item.order?.billing_codes || [])
            .filter((c: any) => c.code_type === 'cpt')
            .map((c: any) => ({ code: c.code, reason: c.reason || '' }))
            .sort((a: any, b: any) => a.code.localeCompare(b.code)),
        }))
    );
    const seen = new Set<string>();
    this.medicalNecessityItems = items.filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }

  trackByMedicalNecessityId(_index: number, item: any): string {
    return item.id;
  }

  private mapDictation(raw: any): PatientDictation {
    const transcriptionRecord = raw.ai_transcriptions?.[0];
    let doctorNotesParsed: any = null;
    let billingParsed: any = null;

    if (transcriptionRecord?.doctor_notes) {
      try {
        doctorNotesParsed = JSON.parse(transcriptionRecord.doctor_notes);
      } catch {
        doctorNotesParsed = null;
      }
    }
    if (transcriptionRecord?.billing_analysis) {
      try {
        billingParsed = JSON.parse(transcriptionRecord.billing_analysis);
      } catch {
        billingParsed = null;
      }
    }

    const dictation: PatientDictation = {
      id: raw.id,
      provider: raw.provider,
      created_at: raw.created_at,
      transcription: transcriptionRecord?.transcription || '',
      doctorNotesParsed,
      billingParsed,
      action_items: raw.action_items || [],
      fileRows: [],
    };
    dictation.fileRows = this.buildFileRows(dictation);

    return dictation;
  }

  // File rows only ever list documents that actually exist — nothing pending/empty gets a row.
  private buildFileRows(d: PatientDictation): FileRow[] {
    const rows: FileRow[] = [];

    if (d.doctorNotesParsed) {
      rows.push({
        key: `notes-${d.id}`,
        icon: 'fa-notes-medical',
        label: 'Doctor Notes',
        view: () => this.viewDoctorNotes(d),
        download: () => this.downloadDoctorNotesPdf(d),
      });
    }

    if (d.transcription) {
      rows.push({
        key: `transcript-${d.id}`,
        icon: 'fa-microphone-alt',
        label: 'Transcription',
        view: () => this.viewTranscription(d),
        download: () => this.downloadTranscriptionPdf(d),
      });
    }

    if (d.billingParsed?.billing_report || d.billingParsed?.cpt_codes?.length || d.billingParsed?.add_on_codes?.length) {
      rows.push({
        key: `summary-${d.id}`,
        icon: 'fa-file-invoice-dollar',
        label: 'Summary',
        view: () => this.viewBillingSummary(d),
        download: () => this.downloadBillingSummaryPdf(d),
      });
    }

    d.action_items.forEach((item: any) => {
      if (!item.order?.lcd_coverage_details) return;
      const itemWithTime = { ...item, dictationTime: d.created_at };
      rows.push({
        key: `mn-${item.id}`,
        icon: 'fa-shield-alt',
        label: 'Medical Necessity',
        subtitle: item.action_item,
        itemId: item.id,
        view: () => this.viewMedicalNecessity(itemWithTime),
        download: () => this.downloadActionItemPdf(itemWithTime),
      });
    });

    return rows;
  }

  trackByFileDictationId(_index: number, d: PatientDictation): string {
    return d.id;
  }

  trackByFileRowKey(_index: number, row: FileRow): string {
    return row.key;
  }

  viewDoctorNotes(d: PatientDictation): void {
    const modalRef = this.modalService.open(DocumentViewerModalComponent, {
      size: 'lg',
      centered: true,
      windowClass: 'document-viewer-modal-window',
    });
    const instance: DocumentViewerModalComponent = modalRef.componentInstance;
    instance.docType = 'doctor-notes';
    instance.timeLabel = this.formatDictationTime(d.created_at);
    instance.patientName = this.currentPatientName;
    instance.patientMrn = this.currentPatientMrn;
    instance.providerName = d.provider?.description || '';
    instance.rawDoctorNotes = d.doctorNotesParsed;
  }

  downloadDoctorNotesPdf(d: PatientDictation): void {
    if (!d.doctorNotesParsed) return;
    this.clinicalDocumentsPdfService.generateDoctorNotesPdf(d.doctorNotesParsed, {
      patientName: this.currentPatientName,
      patientMrn: this.currentPatientMrn,
      providerName: d.provider?.description || '',
    });
  }

  viewTranscription(d: PatientDictation): void {
    const modalRef = this.modalService.open(DocumentViewerModalComponent, {
      size: 'lg',
      centered: true,
      windowClass: 'document-viewer-modal-window',
    });
    const instance: DocumentViewerModalComponent = modalRef.componentInstance;
    instance.docType = 'transcription';
    instance.timeLabel = this.formatDictationTime(d.created_at);
    instance.patientName = this.currentPatientName;
    instance.patientMrn = this.currentPatientMrn;
    instance.providerName = d.provider?.description || '';
    instance.transcriptionText = d.transcription || '';
  }

  downloadTranscriptionPdf(d: PatientDictation): void {
    if (!d.transcription) return;
    this.clinicalDocumentsPdfService.generateTranscriptionPdf(d.transcription, {
      patientName: this.currentPatientName,
      patientMrn: this.currentPatientMrn,
      providerName: d.provider?.description || '',
    });
  }

  viewBillingSummary(d: PatientDictation): void {
    const modalRef = this.modalService.open(DocumentViewerModalComponent, {
      size: 'lg',
      centered: true,
      windowClass: 'document-viewer-modal-window',
    });
    const instance: DocumentViewerModalComponent = modalRef.componentInstance;
    instance.docType = 'billing-summary';
    instance.timeLabel = this.formatDictationTime(d.created_at);
    instance.patientName = this.currentPatientName;
    instance.patientMrn = this.currentPatientMrn;
    instance.providerName = d.provider?.description || '';
    instance.billingSummary = d.billingParsed;
  }

  downloadBillingSummaryPdf(d: PatientDictation): void {
    if (!d.billingParsed) return;
    this.clinicalDocumentsPdfService.generateBillingSummaryPdf(d.billingParsed, {
      patientName: this.currentPatientName,
      patientMrn: this.currentPatientMrn,
      providerName: d.provider?.description || '',
    });
  }

  viewMedicalNecessity(item: any): void {
    const modalRef = this.modalService.open(DocumentViewerModalComponent, {
      size: 'lg',
      centered: true,
      windowClass: 'document-viewer-modal-window',
    });
    const instance: DocumentViewerModalComponent = modalRef.componentInstance;
    instance.docType = 'medical-necessity';
    instance.timeLabel = this.formatDictationTime(item.dictationTime);
    instance.patientName = this.currentPatientName;
    instance.patientMrn = this.currentPatientMrn;
    instance.providerName = item.provider_name || '';
    instance.actionItem = item;
    instance.download.subscribe(() => this.downloadActionItemPdf(item));
  }

  downloadActionItemPdf(item: any): void {
    if (!this.currentPatientId || this.generatingPdfForItemId) return;
    this.generatingPdfForItemId = item.id;
    this.spinnerService.show();
    this.medicalNecessityPdfService
      .generate({
        item,
        patientId: this.currentPatientId,
        patientName: this.currentPatientName,
        patientMrn: this.currentPatientMrn,
        providerName: item.provider_name || '',
      })
      .then(() => {
        this.spinnerService.hide();
        this.generatingPdfForItemId = null;
      })
      .catch(() => {
        this.spinnerService.hide();
        this.generatingPdfForItemId = null;
        this.toastr.error('Failed to generate PDF report');
      });
  }

  // ── Medical Records panel (right column) ─────────────────────────────────────────────────

  private resetRightPanel(): void {
    this.rightPanelLoading = false;
    this.ortMasters = [];
    this.doctorNotes = [];
    this.mriReports = [];
    this.ctReports = [];
    this.operativeReports = [];
    this.reportFileRows = [];
  }

  private fetchDocuments(): void {
    if (!this.currentPatientId) return;
    const requestId = ++this.rightPanelRequestSeq;
    this.rightPanelLoading = true;
    this.ortMasters = [];
    this.doctorNotes = [];
    this.mriReports = [];
    this.ctReports = [];
    this.operativeReports = [];
    this.reportFileRows = [];

    this.audioDictationService
      .getSixMonthsOrtMasters(this.currentPatientId)
      .then((res) => {
        if (requestId !== this.rightPanelRequestSeq) return; // a newer request has since started — drop this stale response
        // Precompute each SafeHtml/SafeResourceUrl wrapper once, here, rather than from the
        // template — bypassSecurityTrust*() returns a NEW object every call, and Angular's
        // property binding re-assigns [srcdoc]/[src] whenever the bound reference changes.
        // Calling it inline in the template means every change-detection tick (e.g. this page's
        // own 5s/8s pollers) hands the iframe a "new" value and forces a full document reload —
        // the frame never finishes rendering before being reloaded again, which shows up as a
        // permanently blank white iframe. Computing it once per fetch keeps the reference stable
        // in between.
        const ortMasters: any[] = res?.data?.ortMasters ?? [];
        this.ortMasters = ortMasters.map((note) => ({ ...note, safeMasterNotes: this.safeHtml(note.master_notes) }));
        this.doctorNotes = res?.data?.doctorNotes ?? [];
        this.mriReports = (res?.data?.mriReports ?? []).map((r) => ({ ...r, safePdfUrl: this.safePdfUrl(r.image_url) }));
        this.ctReports = (res?.data?.ctReports ?? []).map((r) => ({ ...r, safePdfUrl: this.safePdfUrl(r.image_url) }));
        this.operativeReports = (res?.data?.operativeReports ?? []).map((r) => ({
          ...r,
          safePdfUrl: this.safePdfUrl(r.image_url),
        }));
        this.reportFileRows = this.buildReportFileRows();
        this.rightPanelLoading = false;
      })
      .catch((err) => {
        if (requestId !== this.rightPanelRequestSeq) return;
        console.error('Failed to load clinical documentation:', err);
        this.rightPanelLoading = false;
      });
  }

  safeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html || '');
  }

  safePdfUrl(url: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url || '');
  }

  formatOrtDate(note: any): string {
    if (!note?.date) return '';
    const date = moment(note.date).format('MMM D, YYYY');
    const time = note.time ? ` · ${note.time}${note.period ? ' ' + note.period : ''}` : '';
    return `${date}${time}`;
  }

  formatReportDate(report: any): string {
    return report?.date ? moment(report.date).format('MMM D, YYYY') : '';
  }

  mriReportSubtitle(report: any): string {
    const parts = [report?.body_part, report?.side_of_body].filter(Boolean);
    if (report?.contrast) parts.push(`${report.contrast} contrast`);
    return parts.join(' · ') || report?.notes_name || '';
  }

  ctReportSubtitle(report: any): string {
    return report?.body_part || report?.notes_name || '';
  }

  trackByReportId(_index: number, report: any): string {
    return report.id;
  }

  viewReportPdf(url: string): void {
    if (!url) return;
    window.open(url, '_blank');
  }

  downloadReportPdf(url: string, filename: string): void {
    if (!url) return;
    fetch(url)
      .then((res) => res.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(blobUrl);
      })
      .catch(() => window.open(url, '_blank'));
  }

  // Reports aren't tied to any one dictation (the tables only carry patient_id), so they can't
  // slot into the Files panel's per-dictation cards — instead they get one combined card of
  // plain file rows, same View/Download shape as every other file type on this page.
  private buildReportFileRows(): FileRow[] {
    const rows: FileRow[] = [];

    this.mriReports.forEach((r) => {
      rows.push({
        key: `mri-${r.id}`,
        icon: 'fa-magnet',
        label: 'MRI Report',
        subtitle: this.mriReportSubtitle(r),
        view: () => this.viewReportPdf(r.image_url),
        download: () => this.downloadReportPdf(r.image_url, `mri-report-${r.id}.pdf`),
      });
    });
    this.ctReports.forEach((r) => {
      rows.push({
        key: `ct-${r.id}`,
        icon: 'fa-x-ray',
        label: 'CT Report',
        subtitle: this.ctReportSubtitle(r),
        view: () => this.viewReportPdf(r.image_url),
        download: () => this.downloadReportPdf(r.image_url, `ct-report-${r.id}.pdf`),
      });
    });
    this.operativeReports.forEach((r) => {
      rows.push({
        key: `op-${r.id}`,
        icon: 'fa-user-md',
        label: 'Operative Report',
        subtitle: r.notes_name || '',
        view: () => this.viewReportPdf(r.image_url),
        download: () => this.downloadReportPdf(r.image_url, `operative-report-${r.id}.pdf`),
      });
    });

    return rows;
  }

  // ── Print (Clinical Documentation panel) ─────────────────────────────────────────────────

  openPrintModal(): void {
    this.modalService.open(this.printDocumentsModalRef, {
      size: 'lg',
      centered: true,
      windowClass: 'print-documents-modal-window',
    });
  }

  printOrtMaster(note: any): void {
    // master_notes is already a complete standalone HTML document (its own <html>/<head>/
    // <style>) — print it exactly as-is rather than re-wrapping it.
    this.printHtml(note.master_notes);
  }

  printDoctorNote(note: any): void {
    this.printHtml(this.wrapPrintDocument('Doctor Notes', this.doctorNotesBodyHtml(note)));
  }

  printMedicalNecessity(item: any): void {
    this.printHtml(this.wrapPrintDocument('Medical Necessity', this.medicalNecessityBodyHtml(item)));
  }

  /** Prints every currently-loaded document in this panel as one combined job, each starting
   *  on its own page. ORT Master records keep their own embedded <style> (pulled out and
   *  merged into the combined document's <head>) so their tables/headings still render
   *  correctly outside their original standalone document. */
  printAll(): void {
    if (!this.printableDocumentCount) return;

    const pages: string[] = [];
    const extraStyles: string[] = [];
    const addPage = (bodyHtml: string, styles?: string) => {
      const pageBreak = pages.length ? ' style="page-break-before: always;"' : '';
      pages.push(`<div class="doc-print-page"${pageBreak}>${bodyHtml}</div>`);
      if (styles) extraStyles.push(styles);
    };

    this.medicalNecessityItems.forEach((item) => addPage(this.medicalNecessityBodyHtml(item)));
    this.ortMasters.forEach((note) => {
      const { styles, body } = this.extractHtmlParts(note.master_notes);
      addPage(body, styles);
    });
    this.doctorNotes.forEach((note) => addPage(this.doctorNotesBodyHtml(note)));

    this.printHtml(this.wrapPrintDocument('Clinical Documentation', pages.join(''), extraStyles.join('\n')));
  }

  private doctorNotesBodyHtml(note: any): string {
    const sections: [string, string][] = [
      ['History Of Present Illness', note.history_of_present_illness],
      ['Physical Examination', note.physical_examination],
      ['Diagnostics', note.diagnostics],
      ['Assessment / Plan', note.assessment_plan],
    ].filter(([, value]) => this.hasNoteSection(value)) as [string, string][];

    const sectionsHtml = sections
      .map(([label, value]) => `<h2>${this.escapeHtml(label)}</h2><p>${this.escapeHtml(value)}</p>`)
      .join('');

    const meta = this.buildMetaLine([this.formatDictationTime(note.created_at), this.patientMetaPart(), this.mrnMetaPart()]);

    return `<h1>Doctor Notes</h1>${meta}${sectionsHtml || '<p>No content recorded.</p>'}`;
  }

  private medicalNecessityBodyHtml(item: any): string {
    const badge = this.medicalNecessityIndicatorLabel(item);
    const cptHtml = (item.cptCodes || []).length
      ? `<h2>CPT Codes</h2><ul>${item.cptCodes
          .map((c: any) => `<li><strong>${this.escapeHtml(c.code)}</strong>${c.reason ? ' — ' + this.escapeHtml(c.reason) : ''}</li>`)
          .join('')}</ul>`
      : '';
    const coverageHtml = (item.coverageDetails || [])
      .map(
        (row: any) =>
          `<h2>${this.escapeHtml(row.label)}</h2>${row.blocks
            .map((b: any) => `<p>${b.label ? `<strong>${this.escapeHtml(b.label)}</strong> ` : ''}${this.escapeHtml(b.text)}</p>`)
            .join('')}`
      )
      .join('');

    const orderPart = item.order?.order_number ? `Order #${item.order.order_number}` : null;
    const meta = this.buildMetaLine([
      this.formatDictationTime(item.dictationTime || item.created_at),
      orderPart,
      this.patientMetaPart(),
      this.mrnMetaPart(),
    ]);

    return `
      <h1>Medical Necessity</h1>
      ${meta}
      ${badge ? `<p class="badge">${this.escapeHtml(badge)}</p>` : ''}
      <h2>Action Item</h2>
      <p>${this.escapeHtml(item.action_item)}</p>
      ${cptHtml}
      ${coverageHtml}
    `;
  }

  private patientMetaPart(): string | null {
    return this.currentPatientName || null;
  }

  private mrnMetaPart(): string | null {
    return this.currentPatientMrn ? `MRN ${this.currentPatientMrn}` : null;
  }

  private buildMetaLine(rawParts: (string | null | undefined)[]): string {
    const parts = rawParts.filter((p): p is string => !!p).map((p) => this.escapeHtml(p));
    return parts.length ? `<p class="meta">${parts.join(' &middot; ')}</p>` : '';
  }

  private escapeHtml(value: any): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /** Pulls the <style> blocks and body content out of a standalone HTML document (e.g. an ORT
   *  Master's master_notes) so it can be embedded as one page of a larger combined document
   *  without losing its own table/heading styling. */
  private extractHtmlParts(html: string): { styles: string; body: string } {
    const doc = new DOMParser().parseFromString(html || '', 'text/html');
    const styles = Array.from(doc.querySelectorAll('style'))
      .map((el) => el.outerHTML)
      .join('\n');
    const body = doc.body ? doc.body.innerHTML : html || '';
    return { styles, body };
  }

  private wrapPrintDocument(title: string, bodyHtml: string, extraStyles = ''): string {
    return `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<title>${this.escapeHtml(title)}</title>
<style>
  body { font-family: Arial, Helvetica, sans-serif; margin: 24px; color: #1f2937; font-size: 13px; line-height: 1.55; }
  h1 { font-size: 20px; margin: 0 0 4px; color: #111827; }
  h2 { font-size: 13px; margin: 18px 0 6px; color: #1d4ed8; text-transform: uppercase; letter-spacing: 0.04em; }
  p { margin: 0 0 8px; white-space: pre-wrap; }
  p.meta { color: #6b7280; font-size: 12px; margin-bottom: 14px; }
  p.badge { display: inline-block; padding: 3px 10px; border-radius: 6px; background: #eef2ff; color: #1d4ed8; font-weight: 700; font-size: 11px; text-transform: uppercase; letter-spacing: 0.03em; margin-bottom: 10px; }
  ul { margin: 0 0 10px 18px; padding: 0; }
  li { margin-bottom: 3px; }
  .doc-print-page:first-child { margin-top: 0; }
  @page { margin: 16mm; }
</style>
${extraStyles}
</head>
<body>${bodyHtml}</body>
</html>`;
  }

  /** Prints arbitrary HTML via a hidden iframe + native print dialog rather than window.open() —
   *  avoids popup-blocker issues entirely since no new window/tab is created. */
  private printHtml(html: string): void {
    if (!html) return;
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      window.removeEventListener('focus', onFocus);
      setTimeout(() => iframe.remove(), 300);
    };
    // The print dialog is modal — closing it (print or cancel) always refocuses the main
    // window, which is a more reliable cross-browser "done printing" signal than relying on
    // 'afterprint' firing on a cross-frame contentWindow. A generous fallback timer covers the
    // rare case neither fires.
    const onFocus = () => cleanup();

    iframe.onload = () => {
      const win = iframe.contentWindow;
      if (!win) {
        cleanup();
        return;
      }
      window.addEventListener('focus', onFocus, { once: true });
      setTimeout(cleanup, 60000);
      win.focus();
      win.print();
    };

    iframe.srcdoc = html;
  }

  trackByActionItemId(_index: number, item: any): string {
    return item.id;
  }

  trackByOrtMasterId(_index: number, note: any): string {
    return note.id;
  }

  trackByDoctorNoteId(_index: number, note: any): string {
    return note.id;
  }

  // Doctor-notes fields come back as the literal string "NONE" when the AI prompt found no
  // content for that heading (see openAi.js getPromptForNotes) — treat that the same as empty.
  hasNoteSection(value: string): boolean {
    return !!value && value.trim().toUpperCase() !== 'NONE';
  }

  actionItemStatus(item: any): 'accepted' | 'rejected' | 'pending' {
    if (item.order?.lcd_coverage_details) return 'accepted';
    if (item.rejected_ind) return 'rejected';
    return 'pending';
  }

  medicalNecessityIndicator(item: any): MedicalNecessityIndicator {
    return getMedicalNecessityIndicator(item?.order);
  }

  medicalNecessityIndicatorLabel(item: any): string {
    return getMedicalNecessityIndicatorLabel(this.medicalNecessityIndicator(item));
  }

  get initials(): string {
    return getInitials(this.currentPatientName);
  }

  get avatarColor(): string {
    return getAvatarColor(this.currentPatientId || this.currentPatientName || '');
  }

  avatarColorFor(patientId: string): string {
    return getAvatarColor(patientId);
  }

  initialsFor(name: string): string {
    return getInitials(name);
  }

  formatTime(value: string | Date): string {
    return moment(value).format('h:mm:ss.SSS A');
  }

  formatDictationTime(value: string): string {
    return value ? moment(value).format('MMM D, h:mm A') : '';
  }

  closeTab(): void {
    window.close();
  }
}
